<template>
  <div class="customer-create main-content">
    <form action="" @submit.prevent="submitUserData">
      <div class="page-header">
        <h3 class="page-title">{{ isEdit ? $t('accounts.edit_account') : $t('accounts.new_account') }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link v-if="userType=='supplier'" slot="item-title" to="/supplier/dashboard">{{ $t('general.home') }}</router-link>
            <router-link v-else-if="userType=='funder'" slot="item-title" to="/funder/dashboard">{{ $t('general.home') }}</router-link>
            <router-link v-else-if="userType=='anchor'" slot="item-title" to="/anchor/dashboard">{{ $t('general.home') }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link v-if="userType=='supplier'" slot="item-title" to="/supplier/user-managements">{{ $tc('user_management.user', 2) }}</router-link>
            <router-link v-if="userType=='funder'" slot="item-title" to="/funder/user-managements">{{ $tc('user_management.user', 2) }}</router-link>
            <router-link v-if="userType=='anchor'" slot="item-title" to="/anchor/user-managements">{{ $tc('user_management.user', 2) }}</router-link>
          </li>
          <li class="breadcrumb-item">
            {{ isEdit ? $t('user_management.edit_user') : $t('user_management.new_user') }}
          </li>
        </ol>
        <div class="page-actions header-button-container">
          <base-button
            :loading="isLoading"
            :disabled="isLoading"
            :tabindex="23"
            icon="save"
            color="theme"
            type="submit"
          >
            {{ isEdit ? $t('user_management.update_user') : $t('user_management.save_user') }}
          </base-button>
        </div>
      </div>
      <div class="customer-card card">
        <div class="card-body">
          <div class="row">
            <div class="section-title col-sm-2">{{ $t('user_management.basic_info') }}</div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('user_management.display_name') }}</label><span class="text-danger"> *</span>
                <base-input
                  :invalid="$v.formData.name.$error"
                  v-model="formData.name"
                  focus
                  type="text"
                  name="name"
                  tab-index="1"
                  @input="$v.formData.name.$touch()"
                />
                <div v-if="$v.formData.name.$error">
                  <span v-if="!$v.formData.name.required" class="text-danger">
                    {{ $tc('validation.required') }}
                  </span>
                  <span v-if="!$v.formData.name.minLength" class="text-danger">
                    {{ $tc('validation.name_min_length', $v.formData.name.$params.minLength.min, { count: $v.formData.name.$params.minLength.min }) }}
                  </span>
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">{{ $t('user_management.email') }}</label><span class="text-danger"> *</span>
                <base-input
                  :invalid="$v.formData.email.$error"
                  v-model.trim="formData.email"
                  type="text"
                  name="email"
                  tab-index="3"
                  @input="$v.formData.email.$touch()"
                />
                <div v-if="$v.formData.email.$error">
                  <span v-if="!$v.formData.email.email" class="text-danger">
                    {{ $tc('validation.email_incorrect') }}
                  </span>
                  <span v-if="!$v.formData.email.required" class="text-danger">
                    {{ $tc('validation.required') }}
                  </span>
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('user_management.status') }}</label><span class="text-danger"> *</span>
                <base-select
                  v-model="status_change"
                  :options="statusTypes"
                  :custom-label="StatusCustomLabel"
                  :allow-empty="false"
                  :show-labels="false"
                  :tabindex="5"
                  :placeholder="$t('user_management.select_status')"
                  label="name"
                  track-by="id"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('user_management.role') }}</label><span class="text-danger"> *</span>
                <base-select
                  v-model="role_change"
                  :options="roleTypes"
                  :custom-label="RoleCustomLabel"
                  :allow-empty="false"
                  :show-labels="false"
                  :tabindex="5"
                  :placeholder="$t('user_management.select_role')"
                  label="name"
                  track-by="name"
                />
              </div>
            </div>
            <div class="col-sm-5">
              <div class="form-group">
                <label class="form-label">{{ $t('user_management.primary_contact_name') }}</label>
                <base-input
                  v-model.trim="formData.contact_name"
                  :label="$t('accounts.contact_name')"
                  type="text"
                  tab-index="2"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('user_management.phone') }}</label>
                <base-input
                  v-model.trim="formData.phone"
                  type="text"
                  name="phone"
                  tab-index="4"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('user_management.password') }}</label><span class="text-danger"> *</span>
                <base-input
                  v-model="formData.password"
                  :invalid="$v.formData.password.$error"
                  :placeholder="$t('login.enter_password')"
                  type="password"
                  show-password
                  @input="$v.formData.password.$touch()"
                />
                <div v-if="$v.formData.password.$error">
                  <span v-if="!$v.formData.password.required" class="text-danger">{{ $tc('validation.required') }}</span>
                  <span v-if="!$v.formData.password.minLength" class="text-danger"> {{ $tc('validation.password_min_length', $v.formData.password.$params.minLength.min, {count: $v.formData.password.$params.minLength.min}) }} </span>
                  <span v-if="!$v.formData.password.hasNumber" class="text-danger"> {{ $tc('validation.requireNumber') }} </span>
                  <span v-if="!$v.formData.password.hasCapitalCaseLetter" class="text-danger"> {{ $tc('validation.requireCapitalCaseLetter') }} </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MultiSelect from 'vue-multiselect'
import { validationMixin } from 'vuelidate'
import AddressStub from '../../stub/address'
import {hasCapitalCaseLetter, hasNumber } from './../../validators/password'
const { helpers, required, minLength, email, url, maxLength, requiredUnless } = require('vuelidate/lib/validators')

export default {
  components: { MultiSelect },
  mixins: [validationMixin],
  data () {
    return {
      isLoading: false,
      statuses: [],
      roles: [],
      status_change: null,
      role_change: {id: 1, name: 'user'},
      formData: {
        name: null,
        contact_name: null,
        email: null,
        phone: null,
        role: null,
        status: null,
        password: null
      },
      userType: null,
      roleTypes: [
        {id: 1, name: 'user'}
      ]
    }
  },

  validations () {
    if (this.isEdit) {
      return {
        formData: {
          name: {
            required,
            minLength: minLength(3)
          },
          email: {
            email,
            required
          },
          password: {

          }
        }
      }
    } else {
      return {
        formData: {
          name: {
            required,
            minLength: minLength(3)
          },
          email: {
            email,
            required
          },
          password: {
            hasNumber,
            hasCapitalCaseLetter,
            required,
            minLength: minLength(8)
          }
        }
      }
    }
  },
  computed: {
    ...mapGetters('user', [
      'defaultStatus',
      'statusTypes',
      'defaultCountry'
    ]),
    ...mapGetters('currency', [
      'defaultCurrency',
      'currencies'
    ]),
    isEdit () {
      if (this.$route.params.id) {
        return true
      }
      return false
    }
  },
  watch: {
  },
  created () {
    if (localStorage.getItem('role') == 'super_admin') {
      this.userType = 'super_admin'
    } else if (localStorage.getItem('account_type') == 'anchor') {
      this.userType = 'anchor'
    } else if (localStorage.getItem('account_type') == 'funder') {
      this.userType = 'funder'
    } else if (localStorage.getItem('account_type') == 'supplier') {
      this.userType = 'supplier'
    }
  },
  mounted () {
    console.log(this.$route.name)
    console.log(this.isEdit)
    if (this.isEdit) {
      this.loadAccount()
    } else {
      this.status_change = this.defaultStatus
    }
  },
  methods: {
    RoleCustomLabel ({name}) {
      return name.toUpperCase()
    },

    StatusCustomLabel ({name}) {
      return name.toUpperCase()
    },
    ...mapActions('userManagements', [
      'addUser',
      'fetchEditUser',
      'updateUser'
    ]),
    ...mapActions('account', [
      'addAccount',
      'fetchAccounts',
      'fetchAccount',
      'updateAccount'
    ]),
    async loadAccount () {
      let response = await this.fetchEditUser(this.$route.params.id)

      this.formData.id = response.data.account.id
      this.formData.name = response.data.account.name
      this.formData.contact_name = response.data.account.contact_name
      this.formData.email = response.data.account.email
      this.formData.phone = response.data.account.phone
      this.role_change = response.data.role
      this.status_change = response.data.status
    },
    async submitUserData () {
      this.$v.formData.$touch()
      if (this.$v.$invalid) {
        return true
      }
      if (this.isEdit) {
        if (this.role_change) {
          this.formData.role = this.role_change.name
        }
        if (this.status_change) {
          this.formData.status = this.status_change.name
        }
        this.isLoading = true
        try {
          let response = await this.updateUser(this.formData)

          if (response.data.success) {
            window.toastr['success'](this.$t('user_management.updated_message'))
            if (this.userType == 'supplier') {
              this.$router.push('/supplier/user-managements')
            } else if (this.userType == 'anchor') {
              this.$router.push('/anchor/user-managements')
            } else if (this.userType == 'funder') {
              this.$router.push('/funder/user-managements')
            }
            this.isLoading = false
            return true
          } else {
            this.isLoading = false
            if (response.data.emailDuplicate) {
              window.toastr['error'](this.$t('validation.email_already_taken'))
            }
          }
        } catch (err) {
          if (err.response.data.errors.email) {
            this.isLoading = false
            window.toastr['error'](this.$t('validation.email_already_taken'))
          }
        }
      } else {
        this.isLoading = true
        if (this.role_change) {
          this.isLoading = true
          this.formData.role = this.role_change.name
        }
        if (this.status_change) {
          this.isLoading = true
          this.formData.status = this.status_change.name
        }
        try {
          let response = await this.addUser(this.formData)
          if (response.data.success) {
            window.toastr['success'](this.$t('user_management.created_message'))
            if (this.userType == 'supplier') {
              this.$router.push('/supplier/user-managements')
            } else if (this.userType == 'anchor') {
              this.$router.push('/anchor/user-managements')
            } else if (this.userType == 'funder') {
              this.$router.push('/funder/user-managements')
            }
            this.isLoading = false
            return true
          }
        } catch (err) {
          if (err.response.data.errors.email) {
            this.isLoading = false
          }
        }
      }
    }
  }
}
</script>
